import { useMutation, useQuery } from '@tanstack/react-query';

import { selectActiveAccount, useAuthStore } from '@features/auth';
import { queryClient } from '@lib/queryClient';
import type { ApiError } from '@/types/api.types';

import {
  createSpecialty,
  deleteSpecialty,
  getSpecialties,
  updateSpecialty,
} from '../api/specialtiesApi';
import type {
  CreateSpecialtyRequest,
  UpdateSpecialtyRequest,
  Specialty,
  SpecialtyListData,
} from '../types';

const specialtiesKey = (accountId: string) => ['specialties', accountId] as const;

type RemoveVariables = { specialtyId: string; specialtyCode: string };
type UpdateVariables = { specialtyId: string; body: UpdateSpecialtyRequest };

export function useSpecialties() {
  const activeAccount = useAuthStore(selectActiveAccount);
  const accountId = activeAccount?.accountId ?? '';

  const query = useQuery({
    queryKey: specialtiesKey(accountId),
    queryFn: () => getSpecialties(accountId),
    enabled: !!accountId,
  });

  const createMutation = useMutation<Specialty, ApiError, CreateSpecialtyRequest>({
    mutationFn: (body) => createSpecialty(accountId, body),
    onSuccess: (created) => {
      queryClient.setQueryData<SpecialtyListData>(specialtiesKey(accountId), (prev) =>
        prev
          ? { specialties: [...prev.specialties, created], count: prev.count + 1 }
          : { specialties: [created], count: 1 },
      );
    },
  });

  const updateMutation = useMutation<Specialty, ApiError, UpdateVariables>({
    mutationFn: ({ specialtyId, body }) => updateSpecialty(accountId, specialtyId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData<SpecialtyListData>(specialtiesKey(accountId), (prev) =>
        prev
          ? {
              specialties: prev.specialties.map((s) => (s.id === updated.id ? updated : s)),
              count: prev.count,
            }
          : { specialties: [updated], count: 1 },
      );
    },
  });

  const removeMutation = useMutation<void, ApiError, RemoveVariables>({
    mutationFn: ({ specialtyId }) => deleteSpecialty(accountId, specialtyId),
    onSuccess: (_, { specialtyId }) => {
      queryClient.setQueryData<SpecialtyListData>(specialtiesKey(accountId), (prev) =>
        prev
          ? {
              specialties: prev.specialties.filter((s) => s.id !== specialtyId),
              count: prev.count - 1,
            }
          : { specialties: [], count: 0 },
      );
    },
  });

  return {
    specialties: query.data?.specialties ?? [],
    isLoading: query.isLoading,
    fetchError: query.error as ApiError | null,

    create: createMutation.mutate,
    isCreating: createMutation.isPending,
    createError: createMutation.error,
    creatingSpecialtyCode: createMutation.isPending
      ? (createMutation.variables?.specialtyCode ?? null)
      : null,

    update: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    updatingSpecialtyId: updateMutation.isPending
      ? (updateMutation.variables?.specialtyId ?? null)
      : null,

    remove: removeMutation.mutate,
    isRemoving: removeMutation.isPending,
    removeError: removeMutation.error,
    removingSpecialtyCode: removeMutation.isPending
      ? (removeMutation.variables?.specialtyCode ?? null)
      : null,
  };
}
